"use client";

import Image from "next/image";
import { forwardRef, useEffect, useState } from "react";
import { DEFAULT_HOME_HERO_IMAGE } from "@/lib/site-content-fallbacks";
import { cn } from "@/lib/utils";

interface HeroBackgroundProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  kenBurns?: boolean;
  tone?: "dark" | "light";
  overlay?: "default" | "soft" | "minimal";
  objectPosition?: string;
  imageClassName?: string;
  priority?: boolean;
}

export const HeroBackground = forwardRef<HTMLDivElement, HeroBackgroundProps>(
  function HeroBackground(
    {
      src,
      alt,
      fallbackSrc = DEFAULT_HOME_HERO_IMAGE,
      kenBurns = true,
      tone = "dark",
      overlay = "default",
      objectPosition = "center",
      imageClassName,
      priority = true,
    },
    ref
  ) {
    const [activeSrc, setActiveSrc] = useState(src);
    const isLight = tone === "light";
    const isSoft = overlay === "soft";
    const isMinimal = overlay === "minimal";
    const useKenBurns = kenBurns && !isLight && !isSoft;

    useEffect(() => {
      setActiveSrc(src);
    }, [src]);

    const handleImageError = () => {
      if (fallbackSrc && activeSrc !== fallbackSrc) {
        setActiveSrc(fallbackSrc);
      }
    };

    return (
      <div ref={ref} className="absolute inset-0 overflow-hidden" aria-hidden>
        <div
          className={cn(
            isLight || isSoft || isMinimal
              ? "absolute inset-0"
              : "absolute inset-[-8%] h-[116%] w-[116%]",
            useKenBurns && "hero-ken-burns"
          )}
        >
          {activeSrc.startsWith("data:") ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={activeSrc}
              alt={alt}
              className={cn("h-full w-full object-cover", imageClassName)}
              style={{ objectPosition }}
              onError={handleImageError}
            />
          ) : (
            <Image
              key={activeSrc}
              src={activeSrc}
              alt={alt}
              fill
              className={cn("object-cover", imageClassName)}
              style={{ objectPosition }}
              priority={priority}
              sizes="100vw"
              onError={handleImageError}
            />
          )}
        </div>

        {isMinimal ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
            <div className="hero-heading-glow pointer-events-none absolute inset-0 opacity-40" />
          </>
        ) : isSoft ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-black/15 to-black/35" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E20]/20 via-transparent to-[#2E7D32]/15" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
          </>
        ) : isLight ? (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-white/50 via-white/15 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 bg-[rgba(0,0,0,0.45)]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#1B5E20]/35 via-transparent to-[#2E7D32]/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/10" />
            <div className="hero-heading-glow pointer-events-none absolute inset-0" />
          </>
        )}
      </div>
    );
  }
);
